<template>
  <div
    ref="el"
    class="base-richtext"
    :class="[
      isDark ? 'is-dark' : 'is-light',
      {
        'has-prose-styling': isProseEnabled,
        'has-prose-headline-styling': isProseEnabled && isProseHeadlinesEnabled,
        'has-prose-paragraph-styling': isProseEnabled && isProseParagraphsEnabled,
        'is-text-large': isProseEnabled && textSize === 'large',
        'is-text-medium': isProseEnabled && textSize === 'medium',
      },
    ]"
    v-html="content"></div>
</template>

<script lang="ts" setup>
import type { ISbRichtext } from 'storyblok-js-client';
import RichTextResolver from 'storyblok-js-client/richTextResolver';
import { computed, onMounted, useTemplateRef } from 'vue';
import { processExternalLinks, removeEmptyParagraphs, replaceHtmlEntities } from '@seb-platform/shared/utils/text';

const $el = useTemplateRef('el');

const props = withDefaults(
  defineProps<{
    text?: ISbRichtext;
    isDark?: boolean;
    textSize?: 'large' | 'medium';
    removeInlineColorStyles?: boolean;
    /**
     * Add styling to the richtext/prose
     */
    isProseEnabled?: boolean;
    isProseHeadlinesEnabled?: boolean;
    isProseParagraphsEnabled?: boolean;
    preventInlineIcons?: boolean;
  }>(),
  {
    textSize: 'large',
    removeInlineColorStyles: true,
    isProseEnabled: true,
    isProseHeadlinesEnabled: true,
    isProseParagraphsEnabled: true,
  }
);

const resolver = new RichTextResolver();
const content = computed(() => {
  if (!props.text) {
    return '';
  }

  let newContent = resolver.render(props.text);
  newContent = replaceHtmlEntities(newContent);
  // Product names should not break
  newContent = newContent.replaceAll(/(WMF) (\d{3,5}) ([A-z]+)/g, '$1&nbsp;$2&nbsp;$3');

  return newContent;
});

onMounted(() => {
  if ($el.value) {
    removeEmptyParagraphs($el.value);
    processExternalLinks($el.value, { preventInlineIcons: props.preventInlineIcons });
  }
});
</script>

<style src="./SharedBaseRichtext.scss" lang="scss" scoped />
